// src/pages/PdfEditorViewer.js
import React, { useEffect, useRef, useState } from 'react';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import * as pdfjsLib from 'pdfjs-dist';
import './PdfEditorViewer.css';

// Set worker to use local package (no dynamic loading, only local)
// Use the worker from the installed pdfjs-dist package version
if (typeof window !== 'undefined') {
  // Use the worker file from public folder (copied from node_modules during build)
  // This ensures version compatibility with the installed pdfjs-dist package
  pdfjsLib.GlobalWorkerOptions.workerSrc = `${window.location.origin}/pdf.worker.min.mjs`;
}

// Text Input Dialog Component
function TextInputDialog({ onAdd, onCancel }) {
  const [text, setText] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <div style={{
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      background: 'white',
      padding: 16,
      border: '2px solid #B99040',
      borderRadius: 8,
      zIndex: 1000,
      boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
    }}>
      <input
        ref={inputRef}
        type="text"
        placeholder="Enter text..."
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && text.trim()) {
            onAdd(text);
          } else if (e.key === 'Escape') {
            onCancel();
          }
        }}
        style={{ padding: 8, fontSize: 14, width: 300 }}
      />
      <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
        <button onClick={() => text.trim() && onAdd(text)} style={{ padding: '6px 12px', background: '#4caf50', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
          Add
        </button>
        <button onClick={onCancel} style={{ padding: '6px 12px', background: '#666', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

const TOOLS = {
  SELECT: 'select',
  TEXT: 'text',
  IMAGE: 'image',
  REDACT: 'redact',
  SIGNATURE: 'signature',
  HIGHLIGHT: 'highlight',
  ANNOTATE: 'annotate'
};

export default function PdfEditorViewer() {
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfBytes, setPdfBytes] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [fileName, setFileName] = useState('document.pdf');
  const [loadingError, setLoadingError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editingMode, setEditingMode] = useState(false);
  
  // Editing state
  const [activeTool, setActiveTool] = useState(TOOLS.SELECT);
  const [edits, setEdits] = useState([]); // Array of edit operations
  const [selectedPage, setSelectedPage] = useState(1);
  const [zoom, setZoom] = useState(1.2);
  const [textElements, setTextElements] = useState([]); // {id, page, x, y, text, fontSize, color, font}
  const [imageElements, setImageElements] = useState([]); // {id, page, x, y, imageData, width, height}
  const [redactions, setRedactions] = useState([]); // {id, page, x, y, width, height}
  const [signatures, setSignatures] = useState([]); // {id, page, x, y, imageData, width, height}
  const [pageOperations, setPageOperations] = useState([]); // {type: 'delete'|'rotate'|'reorder', page, data}
  const [textInput, setTextInput] = useState({ show: false, x: 0, y: 0, page: 1 });
  const [textEdit, setTextEdit] = useState({ show: false, text: '', x: 0, y: 0, page: 1, width: 0, height: 0 });
  const [textProps, setTextProps] = useState({ fontSize: 12, color: '#000000', font: 'Helvetica' });
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [selectedElement, setSelectedElement] = useState(null); // {type: 'text'|'image'|'redaction'|'signature', id}
  const [dragging, setDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizing, setResizing] = useState(null); // {type, id, handle, startX, startY, startWidth, startHeight}
  
  // New state for enhancements
  const [viewMode, setViewMode] = useState('continuous'); // 'continuous' | 'single' | 'two-page'
  const [currentPage, setCurrentPage] = useState(1);
  const [thumbnailCache, setThumbnailCache] = useState(new Map());
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(-1);
  const [highlightedText, setHighlightedText] = useState([]);
  const [highlightColor, setHighlightColor] = useState('#ffff00');
  
  const pageRefs = useRef({});
  const signatureCanvasRef = useRef(null);
  const searchInputRef = useRef(null);


  // Keyboard shortcuts
  useEffect(() => {
    function handleKeyDown(e) {
      if (!editingMode) return;
      
      // Delete selected element with Delete or Backspace
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedElement) {
        e.preventDefault();
        if (window.confirm('Delete selected element?')) {
          deleteElement(selectedElement.type, selectedElement.id);
        }
      }
      
      // Escape to deselect
      if (e.key === 'Escape') {
        setSelectedElement(null);
      }
    }
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editingMode, selectedElement]);

  const [pdfDoc, setPdfDoc] = useState(null);
  const pageCanvasesRef = useRef({});
  
  // Render a single page to canvas
  const renderPage = React.useCallback(async (pageNum, canvasElement, textLayerElement) => {
    if (!pdfDoc || !canvasElement) return;
    
    try {
      const page = await pdfDoc.getPage(pageNum);
      const viewport = page.getViewport({ scale: zoom });
      
      const context = canvasElement.getContext('2d');
      canvasElement.height = viewport.height;
      canvasElement.width = viewport.width;
      
      await page.render({
        canvasContext: context,
        viewport: viewport
      }).promise;
      
      // Render text layer for text selection (only when not in editing mode)
      if (textLayerElement && !editingMode) {
        try {
          const textContent = await page.getTextContent();
          const textLayerDiv = textLayerElement;
          textLayerDiv.innerHTML = ''; // Clear previous content
          textLayerDiv.style.width = `${viewport.width}px`;
          textLayerDiv.style.height = `${viewport.height}px`;
          textLayerDiv.style.position = 'absolute';
          textLayerDiv.style.top = '0';
          textLayerDiv.style.left = '0';
          textLayerDiv.style.overflow = 'hidden';
          textLayerDiv.style.opacity = '0';
          textLayerDiv.style.lineHeight = '1';
          textLayerDiv.style.userSelect = 'text';
          textLayerDiv.style.pointerEvents = 'auto';
          textLayerDiv.style.color = 'transparent';
          
          // Create text spans for each text item
          // PDF coordinates: (0,0) is bottom-left, CSS: (0,0) is top-left
          // Need to convert Y coordinates: cssY = viewportHeight - pdfY
          const textItems = textContent.items;
          for (let i = 0; i < textItems.length; i++) {
            const textItem = textItems[i];
            if (!textItem.str || textItem.str.trim() === '') continue;
            
            const tx = textItem.transform;
            // Transform matrix: [a, b, c, d, e, f]
            // tx[0] = a (horizontal scale), tx[3] = d (vertical scale)
            // tx[4] = e (horizontal translation), tx[5] = f (vertical translation in PDF coords)
            
            const span = document.createElement('span');
            span.textContent = textItem.str;
            span.setAttribute('role', 'presentation');
            span.style.position = 'absolute';
            span.style.left = `${tx[4]}px`;
            // Convert PDF Y coordinate (bottom-left origin) to CSS Y coordinate (top-left origin)
            // PDF Y is measured from bottom, CSS Y is measured from top
            // We need to flip: cssTop = viewportHeight - pdfY
            // But also account for the text height (tx[3] is the vertical scale)
            const textHeight = Math.abs(tx[3]);
            span.style.top = `${viewport.height - tx[5] - textHeight}px`;
            span.style.fontSize = `${Math.abs(tx[0])}px`;
            span.style.fontFamily = textItem.fontName || 'sans-serif';
            // Flip Y components of transform matrix for CSS coordinate system
            span.style.transform = `matrix(${tx[0]}, ${-tx[1]}, ${tx[2]}, ${-tx[3]}, 0, 0)`;
            span.style.transformOrigin = '0% 0%';
            span.style.whiteSpace = 'pre';
            span.style.cursor = 'text';
            span.style.color = 'transparent';
            span.style.userSelect = 'text';
            span.style.pointerEvents = 'auto';
            span.style.lineHeight = '1';
            span.style.letterSpacing = '0';
            textLayerDiv.appendChild(span);
          }
          
          console.log(`✅ [PDF Viewer] Text layer rendered for page ${pageNum} with ${textItems.length} text items`);
        } catch (textErr) {
          console.warn(`⚠️ [PDF Viewer] Failed to render text layer for page ${pageNum}:`, textErr);
        }
      } else if (textLayerElement && editingMode) {
        // Clear text layer when in editing mode
        textLayerElement.innerHTML = '';
        textLayerElement.style.pointerEvents = 'none';
      }
      
      console.log(`✅ [PDF Viewer] Page ${pageNum} rendered`);
    } catch (err) {
      console.error(`❌ [PDF Viewer] Failed to render page ${pageNum}:`, err);
      setLoadingError(`Page ${pageNum} render error: ${err?.message || String(err)}`);
    }
  }, [pdfDoc, zoom, editingMode]);
  
  // Load PDF using pdf.js directly (completely avoids CORS - uses bytes directly)
  useEffect(() => {
    if (!pdfBytes) {
      setPdfDoc(null);
      setNumPages(null);
      return;
    }
    
    async function loadPdf() {
      try {
        console.log('📥 [PDF Viewer] Loading PDF with pdf.js directly from bytes');
        
        // Clone the bytes to avoid ArrayBuffer detachment issues
        // Create a completely fresh copy - this ensures the ArrayBuffer won't be detached
        const bytesCopy = new Uint8Array(pdfBytes.length);
        bytesCopy.set(pdfBytes, 0);
        
        // Use cloned bytes directly - no URL, no CORS issues
        // Pass the buffer from the new Uint8Array (which has its own ArrayBuffer)
        const loadingTask = pdfjsLib.getDocument({ 
          data: bytesCopy.buffer,
          verbosity: 0 // Reduce console noise
        }).promise;
        
        const pdf = await loadingTask;
        const numPages = pdf.numPages;
        console.log(`✅ [PDF Viewer] PDF loaded successfully: ${numPages} pages`);
        
        setPdfDoc(pdf);
    setNumPages(numPages);
    setLoadingError(null);
        
      } catch (error) {
        console.error('❌ [PDF Viewer] Failed to load PDF:', { 
          error, 
          message: error?.message, 
          stack: error?.stack,
          name: error?.name 
        });
        setLoadingError(`Failed to load PDF: ${error?.message || String(error)}`);
        setPdfDoc(null);
        setNumPages(null);
      }
    }
    
    loadPdf();
  }, [pdfBytes]);
  
  // Render all pages when PDF or zoom changes
  useEffect(() => {
    if (!pdfDoc || !numPages) return;
    
    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      for (let i = 1; i <= numPages; i++) {
        const canvas = document.getElementById(`pdf-page-${i}`);
        const textLayer = document.getElementById(`pdf-text-layer-${i}`);
        if (canvas) {
          renderPage(i, canvas, textLayer);
        }
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [pdfDoc, numPages, zoom, editingMode, renderPage]);

  // Generate thumbnail for a page
  const generateThumbnail = React.useCallback(async (pageNum, scale = 0.25) => {
    if (!pdfDoc) return null;
    if (thumbnailCache.has(pageNum)) {
      return thumbnailCache.get(pageNum);
    }
    
    try {
      const page = await pdfDoc.getPage(pageNum);
      const viewport = page.getViewport({ scale });
      
      const canvas = document.createElement('canvas');
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const context = canvas.getContext('2d');
      
      await page.render({
        canvasContext: context,
        viewport: viewport
      }).promise;
      
      const dataUrl = canvas.toDataURL('image/png');
      setThumbnailCache(prev => new Map(prev).set(pageNum, dataUrl));
      return dataUrl;
    } catch (err) {
      console.error(`Failed to generate thumbnail for page ${pageNum}:`, err);
      return null;
    }
  }, [pdfDoc, thumbnailCache]);

  // Scroll to a specific page
  const scrollToPage = React.useCallback((pageNum) => {
    const pageElement = document.querySelector(`[data-page-number="${pageNum}"]`);
    if (pageElement) {
      pageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setCurrentPage(pageNum);
      setSelectedPage(pageNum);
    }
  }, []);

  // Go to a specific page
  const goToPage = React.useCallback((pageNum) => {
    if (pageNum >= 1 && pageNum <= numPages) {
      setCurrentPage(pageNum);
      scrollToPage(pageNum);
    }
  }, [numPages, scrollToPage]);

  // Calculate zoom to fit width
  const calculateFitWidthZoom = React.useCallback(async () => {
    if (!pdfDoc || !numPages) return 1.2;
    try {
      const page = await pdfDoc.getPage(1);
      const viewport = page.getViewport({ scale: 1 });
      const container = document.querySelector('.pdf-document');
      if (container) {
        const containerWidth = container.clientWidth - 40; // Account for padding
        return Math.min(containerWidth / viewport.width, 3);
      }
    } catch (err) {
      console.error('Failed to calculate fit width zoom:', err);
    }
    return 1.2;
  }, [pdfDoc, numPages]);

  // Calculate zoom to fit height
  const calculateFitHeightZoom = React.useCallback(async () => {
    if (!pdfDoc || !numPages) return 1.2;
    try {
      const page = await pdfDoc.getPage(1);
      const viewport = page.getViewport({ scale: 1 });
      const container = document.querySelector('.pdf-document');
      if (container) {
        const containerHeight = container.clientHeight - 40; // Account for padding
        return Math.min(containerHeight / viewport.height, 3);
      }
    } catch (err) {
      console.error('Failed to calculate fit height zoom:', err);
    }
    return 1.2;
  }, [pdfDoc, numPages]);

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      console.log('📤 [PDF Editor] Loading PDF:', file.name);
      
      // Reset editing state
      resetEditingState();
      setEditingMode(false);
      
      // Load file locally
      const arrayBuffer = await file.arrayBuffer();
      const pdfBytes = new Uint8Array(arrayBuffer);
      
      // Calculate page count
      try {
        const pdfDoc = await PDFDocument.load(arrayBuffer);
        const pagesCount = pdfDoc.getPageCount();
        console.log(`📄 [PDF Editor] Loaded PDF with ${pagesCount} pages`);
        setNumPages(pagesCount);
    } catch (err) {
        console.warn('⚠️ [PDF Editor] Could not calculate page count:', err);
      }
      
      // Create File object for reference
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const fileObj = new File([blob], file.name, { type: 'application/pdf' });
      
      setPdfFile(fileObj);
      setPdfBytes(pdfBytes);
      setFileName(file.name);
      setLoadingError(null);
    } catch (err) {
      console.error('❌ [PDF Editor] Failed to load PDF:', { error: err });
      setPdfFile(null);
      setPdfBytes(null);
      setLoadingError(`Failed to load PDF: ${err?.message || String(err)}`);
      alert('Failed to load PDF. Check console for details.');
    }
  }

  function resetEditingState() {
    setActiveTool(TOOLS.SELECT);
    setEdits([]);
    setSelectedPage(1);
    setTextElements([]);
    setImageElements([]);
    setRedactions([]);
    setSignatures([]);
    setPageOperations([]);
    setTextInput({ show: false, x: 0, y: 0, page: 1 });
    setUndoStack([]);
    setRedoStack([]);
  }

  function handleClear() {
    if (!window.confirm('Clear current PDF and start over?')) return;
    setPdfFile(null);
    setPdfBytes(null);
    setNumPages(null);
    setFileName('document.pdf');
    resetEditingState();
    setEditingMode(false);
    setLoadingError(null);
  }

  function handlePageClick(e, pageNumber) {
    // Check if text is selected first (for editing existing text)
    const selection = window.getSelection();
    const hasSelection = selection && selection.toString().trim().length > 0;
    
    if (hasSelection && editingMode && activeTool === TOOLS.TEXT) {
      // User selected text and wants to edit it
      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      const pageRect = e.currentTarget.getBoundingClientRect();
      const x = (rect.left - pageRect.left) / zoom;
      const y = (pageRect.height - (rect.top - pageRect.top)) / zoom;
      const width = rect.width / zoom;
      const height = rect.height / zoom;
      
      setTextEdit({
        show: true,
        text: selection.toString(),
        x,
        y,
        page: pageNumber,
        width,
        height
      });
      selection.removeAllRanges();
      e.preventDefault();
      e.stopPropagation();
        return;
    }
    
    if (!editingMode || activeTool === TOOLS.SELECT) {
      return;
    }
    
    // Don't interfere with text selection
    if (hasSelection) {
        return;
    }
    
    e.preventDefault();
    e.stopPropagation();
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / zoom;
    const y = (rect.height - (e.clientY - rect.top)) / zoom; // PDF coordinates are bottom-up
    
    if (activeTool === TOOLS.TEXT) {
      setTextInput({ show: true, x, y, page: pageNumber });
    } else if (activeTool === TOOLS.REDACT) {
      addRedaction(pageNumber, x, y, 100, 30);
    } else if (activeTool === TOOLS.SIGNATURE) {
      handleSignaturePlace(pageNumber, x, y);
    } else if (activeTool === TOOLS.IMAGE) {
      handleImagePlace(pageNumber, x, y);
    }
  }
  
  // Add double-click handler for easier text editing
  function handlePageDoubleClick(e, pageNumber) {
    if (!editingMode || activeTool !== TOOLS.TEXT) return;
    
    const selection = window.getSelection();
    if (selection && selection.toString().trim().length > 0) {
      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      const pageRect = e.currentTarget.getBoundingClientRect();
      const x = (rect.left - pageRect.left) / zoom;
      const y = (pageRect.height - (rect.top - pageRect.top)) / zoom;
      const width = rect.width / zoom;
      const height = rect.height / zoom;
      
      setTextEdit({
        show: true,
        text: selection.toString(),
        x,
        y,
        page: pageNumber,
        width,
        height
      });
      selection.removeAllRanges();
      e.preventDefault();
      e.stopPropagation();
    }
  }
  
  function handleEditText(newText) {
    if (!textEdit.show || !newText.trim()) return;
    
    // Add redaction over old text area
    addRedaction(textEdit.page, textEdit.x, textEdit.y, textEdit.width || 100, textEdit.height || 20);
    
    // Add new text at the same position
    addTextElement(textEdit.page, textEdit.x, textEdit.y, newText);
    
    setTextEdit({ show: false, text: '', x: 0, y: 0, page: 1, width: 0, height: 0 });
  }

  function addTextElement(page, x, y, text) {
    const newElement = { id: Date.now(), page, x, y, text, ...textProps };
    setTextElements([...textElements, newElement]);
    addToUndoStack({ type: 'addText', element: newElement });
    setTextInput({ show: false, x: 0, y: 0, page: 1 });
    setSelectedElement({ type: 'text', id: newElement.id });
    setActiveTool(TOOLS.SELECT); // Switch to select tool after adding
  }

  function addRedaction(page, x, y, width, height) {
    const newRedaction = { id: Date.now(), page, x, y, width, height };
    setRedactions([...redactions, newRedaction]);
    addToUndoStack({ type: 'addRedaction', element: newRedaction });
  }

  function handleSignaturePlace(page, x, y) {
    if (signatureCanvasRef.current) {
      const canvas = signatureCanvasRef.current;
      const imageData = canvas.toDataURL('image/png');
      const newSignature = { id: Date.now(), page, x, y, imageData, width: 150, height: 60 };
      setSignatures([...signatures, newSignature]);
      addToUndoStack({ type: 'addSignature', element: newSignature });
    } else {
      alert('Please draw a signature first using the signature pad.');
    }
  }

  function handleImagePlace(page, x, y) {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (file) {
        try {
          const reader = new FileReader();
          reader.onload = (event) => {
            const imageData = event.target.result;
            const img = new Image();
            img.onload = () => {
              // Calculate size maintaining aspect ratio, max 200px
              const maxSize = 200;
              let width = img.width;
              let height = img.height;
              if (width > maxSize || height > maxSize) {
                const ratio = Math.min(maxSize / width, maxSize / height);
                width = width * ratio;
                height = height * ratio;
              }
              
              const newImage = { 
                id: Date.now(), 
                page, 
                x, 
                y, 
                imageData, 
                width, 
                height 
              };
              setImageElements([...imageElements, newImage]);
              addToUndoStack({ type: 'addImage', element: newImage });
              setSelectedElement({ type: 'image', id: newImage.id });
              setActiveTool(TOOLS.SELECT); // Switch to select tool after adding
            };
            img.onerror = () => {
              alert('Failed to load image. Please try a different image.');
            };
            img.src = imageData;
          };
          reader.onerror = () => {
            alert('Failed to read image file.');
          };
          reader.readAsDataURL(file);
      } catch (err) {
          console.error('Error loading image:', err);
          alert('Failed to load image. Please try again.');
        }
      }
    };
    input.click();
  }
  
  // Element manipulation functions
  function updateElementPosition(type, id, newX, newY) {
    if (type === 'text') {
      setTextElements(textElements.map(el => 
        el.id === id ? { ...el, x: newX, y: newY } : el
      ));
    } else if (type === 'image') {
      setImageElements(imageElements.map(el => 
        el.id === id ? { ...el, x: newX, y: newY } : el
      ));
    } else if (type === 'redaction') {
      setRedactions(redactions.map(el => 
        el.id === id ? { ...el, x: newX, y: newY } : el
      ));
    } else if (type === 'signature') {
      setSignatures(signatures.map(el => 
        el.id === id ? { ...el, x: newX, y: newY } : el
      ));
    }
  }
  
  function updateElementSize(type, id, newWidth, newHeight) {
    if (type === 'image') {
      setImageElements(imageElements.map(el => 
        el.id === id ? { ...el, width: newWidth, height: newHeight } : el
      ));
    } else if (type === 'redaction') {
      setRedactions(redactions.map(el => 
        el.id === id ? { ...el, width: newWidth, height: newHeight } : el
      ));
    } else if (type === 'signature') {
      setSignatures(signatures.map(el => 
        el.id === id ? { ...el, width: newWidth, height: newHeight } : el
      ));
    }
  }
  
  function deleteElement(type, id) {
    if (type === 'text') {
      setTextElements(textElements.filter(el => el.id !== id));
    } else if (type === 'image') {
      setImageElements(imageElements.filter(el => el.id !== id));
    } else if (type === 'redaction') {
      setRedactions(redactions.filter(el => el.id !== id));
    } else if (type === 'signature') {
      setSignatures(signatures.filter(el => el.id !== id));
    }
    setSelectedElement(null);
  }
  
  // Drag handlers
  function handleElementMouseDown(e, type, id, elementX, elementY) {
    if (activeTool !== TOOLS.SELECT) return;
    
    e.stopPropagation();
    e.preventDefault();
    
    setSelectedElement({ type, id });
    setDragging(true);
    
    const pageEl = e.currentTarget.closest('[data-page-number]');
    if (pageEl) {
      const pageRect = pageEl.getBoundingClientRect();
      const mouseX = (e.clientX - pageRect.left) / zoom;
      const mouseY = (pageRect.height - (e.clientY - pageRect.top)) / zoom;
      setDragOffset({ x: mouseX - elementX, y: mouseY - elementY });
    }
  }
  
  useEffect(() => {
    if (!dragging) return;
    
    function handleMouseMove(e) {
      if (!selectedElement) return;
      
      const pageEl = document.querySelector(`[data-page-number="${selectedPage}"]`);
      if (!pageEl) return;
      
      const pageRect = pageEl.getBoundingClientRect();
      const newX = (e.clientX - pageRect.left) / zoom - dragOffset.x;
      const newY = (pageRect.height - (e.clientY - pageRect.top)) / zoom - dragOffset.y;
      
      updateElementPosition(selectedElement.type, selectedElement.id, Math.max(0, newX), Math.max(0, newY));
    }
    
    function handleMouseUp() {
      setDragging(false);
      setDragOffset({ x: 0, y: 0 });
    }
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging, selectedElement, dragOffset, zoom, selectedPage]);

  // Resize handlers
  function handleResizeStart(e, element, handle, type) {
    e.stopPropagation();
    e.preventDefault();
    
    const pageEl = e.currentTarget.closest('[data-page-number]');
    if (!pageEl) return;
    
    const pageRect = pageEl.getBoundingClientRect();
    const mouseX = (e.clientX - pageRect.left) / zoom;
    const mouseY = (pageRect.height - (e.clientY - pageRect.top)) / zoom;
    
    setResizing({
      type,
      id: element.id,
      handle,
      startX: mouseX,
      startY: mouseY,
      startWidth: element.width || 100,
      startHeight: element.height || 30,
      startXPos: element.x,
      startYPos: element.y
    });
  }

  useEffect(() => {
    if (!resizing) return;
    
    function handleResizeMove(e) {
      const pageEl = document.querySelector(`[data-page-number="${selectedPage}"]`);
      if (!pageEl) return;
      
      const pageRect = pageEl.getBoundingClientRect();
      const mouseX = (e.clientX - pageRect.left) / zoom;
      const mouseY = (pageRect.height - (e.clientY - pageRect.top)) / zoom;
      
      const deltaX = mouseX - resizing.startX;
      const deltaY = mouseY - resizing.startY;
      
      let newWidth = resizing.startWidth;
      let newHeight = resizing.startHeight;
      let newX = resizing.startXPos;
      let newY = resizing.startYPos;
      
      const handle = resizing.handle;
      if (handle.includes('e')) {
        newWidth = Math.max(10, resizing.startWidth + deltaX);
      }
      if (handle.includes('w')) {
        newWidth = Math.max(10, resizing.startWidth - deltaX);
        newX = resizing.startXPos + deltaX;
      }
      if (handle.includes('s')) {
        newHeight = Math.max(10, resizing.startHeight - deltaY);
      }
      if (handle.includes('n')) {
        newHeight = Math.max(10, resizing.startHeight + deltaY);
        newY = resizing.startYPos - deltaY;
      }
      
      updateElementSize(resizing.type, resizing.id, newWidth, newHeight);
      if (newX !== resizing.startXPos || newY !== resizing.startYPos) {
        updateElementPosition(resizing.type, resizing.id, newX, newY);
      }
    }
    
    function handleResizeEnd() {
      setResizing(null);
    }
    
    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);
    
    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
    };
  }, [resizing, zoom, selectedPage]);

  // Render resize handles for selected element
  function renderResizeHandles(element, type, pageNum) {
    if (!element || !selectedElement || selectedElement.type !== type || selectedElement.id !== element.id) {
      return null;
    }
    
    const handles = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
    const width = (element.width || 100) * zoom;
    const height = (element.height || 30) * zoom;
    const left = element.x * zoom;
    const bottom = element.y * zoom;
    
    return handles.map(handle => {
      let style = {
        position: 'absolute',
        width: '8px',
        height: '8px',
        background: '#B99040',
        border: '1px solid white',
        borderRadius: '50%',
        cursor: `${handle}-resize`,
        zIndex: 1000,
        pointerEvents: 'auto'
      };
      
      if (handle.includes('n')) {
        style.bottom = `${bottom + height}px`;
      } else if (handle.includes('s')) {
        style.bottom = `${bottom}px`;
      } else {
        style.bottom = `${bottom + height / 2}px`;
      }
      
      if (handle.includes('e')) {
        style.left = `${left + width}px`;
      } else if (handle.includes('w')) {
        style.left = `${left}px`;
      } else {
        style.left = `${left + width / 2}px`;
      }
      
      if (handle.includes('n') || handle.includes('s')) {
        style.transform = 'translateY(50%)';
      }
      if (handle.includes('e') || handle.includes('w')) {
        style.transform = 'translateX(50%)';
      }
      if (handle.length === 2) {
        style.transform = 'translate(-50%, 50%)';
      }
      
      return (
        <div
          key={handle}
          className="resize-handle"
          style={style}
          onMouseDown={(e) => handleResizeStart(e, element, handle, type)}
        />
      );
    });
  }

  // Highlight text functionality
  function handleTextSelection() {
    if (activeTool !== TOOLS.HIGHLIGHT) return;
    
    const selection = window.getSelection();
    if (!selection || selection.toString().trim().length === 0) return;
    
    const range = selection.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    const pageEl = document.querySelector('.pdf-document')?.querySelector('[data-page-number]');
    if (!pageEl) return;
    
    const pageRect = pageEl.getBoundingClientRect();
    const pageNum = parseInt(pageEl.getAttribute('data-page-number')) || 1;
    const x = (rect.left - pageRect.left) / zoom;
    const y = (pageRect.height - (rect.top - pageRect.top)) / zoom;
    const width = rect.width / zoom;
    const height = rect.height / zoom;
    
    const newHighlight = {
      id: Date.now(),
      page: pageNum,
      x,
      y,
      width,
      height,
      color: highlightColor
    };
    
    setHighlightedText([...highlightedText, newHighlight]);
    addToUndoStack({ type: 'addHighlight', element: newHighlight });
    selection.removeAllRanges();
  }

  // Search functionality
  const searchInPdf = React.useCallback(async (query) => {
    if (!pdfDoc || !query.trim()) {
      setSearchResults([]);
        return;
    }

    const results = [];
    const lowerQuery = query.toLowerCase();
    
    try {
      for (let i = 1; i <= numPages; i++) {
        const page = await pdfDoc.getPage(i);
        const textContent = await page.getTextContent();
        const text = textContent.items.map(item => item.str).join(' ');
        
        if (text.toLowerCase().includes(lowerQuery)) {
          const matches = [...text.matchAll(new RegExp(lowerQuery, 'gi'))];
          matches.forEach(match => {
            results.push({
              page: i,
              match: match[0],
              index: match.index
            });
          });
        }
      }
      
      setSearchResults(results);
      setCurrentMatchIndex(results.length > 0 ? 0 : -1);
      } catch (err) {
      console.error('Search failed:', err);
      setSearchResults([]);
    }
  }, [pdfDoc, numPages]);

  const navigateToMatch = React.useCallback((matchIndex) => {
    if (matchIndex < 0 || matchIndex >= searchResults.length) return;
    
    const match = searchResults[matchIndex];
    goToPage(match.page);
    setCurrentMatchIndex(matchIndex);
  }, [searchResults, goToPage]);

  // Keyboard shortcuts
  useEffect(() => {
    function handleKeyDown(e) {
      // Ctrl+F / Cmd+F: Focus search
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        searchInputRef.current?.focus();
        return;
      }
      
      // Ctrl+G / Cmd+G: Go to page
      if ((e.ctrlKey || e.metaKey) && e.key === 'g') {
        e.preventDefault();
        const page = window.prompt(`Go to page (1-${numPages}):`);
        if (page) {
          const pageNum = parseInt(page);
          if (pageNum >= 1 && pageNum <= numPages) {
            goToPage(pageNum);
          }
        }
      return;
    }

      // Escape: Close dialogs, deselect
      if (e.key === 'Escape') {
        setTextInput({ show: false, x: 0, y: 0, page: 1 });
        setTextEdit({ show: false, text: '', x: 0, y: 0, page: 1, width: 0, height: 0 });
        setSelectedElement(null);
        return;
      }
      
      // Arrow keys for navigation (only when not in input)
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
        
        e.preventDefault();
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          if (currentPage > 1) goToPage(currentPage - 1);
        } else {
          if (currentPage < numPages) goToPage(currentPage + 1);
        }
        return;
      }
      
      // Search navigation
      if (searchResults.length > 0) {
        if (e.key === 'F3' || ((e.ctrlKey || e.metaKey) && e.key === 'g' && e.shiftKey)) {
          e.preventDefault();
          navigateToMatch((currentMatchIndex + 1) % searchResults.length);
          return;
        }
      }
    }
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentPage, numPages, goToPage, searchResults, currentMatchIndex, navigateToMatch]);

  function deletePage(pageNumber) {
    if (!window.confirm(`Delete page ${pageNumber}?`)) return;
    setPageOperations([...pageOperations, { type: 'delete', page: pageNumber }]);
    addToUndoStack({ type: 'deletePage', page: pageNumber });
  }

  function rotatePage(pageNumber, degrees) {
    setPageOperations([...pageOperations, { type: 'rotate', page: pageNumber, degrees }]);
    addToUndoStack({ type: 'rotatePage', page: pageNumber, degrees });
  }

  function addBlankPage() {
    setPageOperations([...pageOperations, { type: 'add', page: numPages + 1 }]);
    addToUndoStack({ type: 'addPage' });
  }

  function addToUndoStack(operation) {
    setUndoStack([...undoStack, operation]);
    setRedoStack([]); // Clear redo when new operation is done
  }

  function handleUndo() {
    if (undoStack.length === 0) return;
    const lastOp = undoStack[undoStack.length - 1];
    setRedoStack([...redoStack, lastOp]);
    setUndoStack(undoStack.slice(0, -1));
    
    // Reverse the operation
    if (lastOp.type === 'addText') {
      setTextElements(textElements.filter(e => e.id !== lastOp.element.id));
    } else if (lastOp.type === 'addRedaction') {
      setRedactions(redactions.filter(r => r.id !== lastOp.element.id));
    } else if (lastOp.type === 'addImage') {
      setImageElements(imageElements.filter(i => i.id !== lastOp.element.id));
    } else if (lastOp.type === 'addSignature') {
      setSignatures(signatures.filter(s => s.id !== lastOp.element.id));
    }
  }

  function handleRedo() {
    if (redoStack.length === 0) return;
    const lastOp = redoStack[redoStack.length - 1];
    setUndoStack([...undoStack, lastOp]);
    setRedoStack(redoStack.slice(0, -1));
    
    // Re-apply the operation
    if (lastOp.type === 'addText') {
      setTextElements([...textElements, lastOp.element]);
    } else if (lastOp.type === 'addRedaction') {
      setRedactions([...redactions, lastOp.element]);
    } else if (lastOp.type === 'addImage') {
      setImageElements([...imageElements, lastOp.element]);
    } else if (lastOp.type === 'addSignature') {
      setSignatures([...signatures, lastOp.element]);
    }
  }

  async function handleDownload() {
    if (!pdfBytes) {
      alert('No PDF loaded. Please upload a PDF first.');
      return;
    }
    
    setSaving(true);
    try {
      const pdfDoc = await PDFDocument.load(pdfBytes);
      const pages = pdfDoc.getPages();
      const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const helveticaBoldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
      
      // Apply page operations
      for (const op of pageOperations) {
        if (op.type === 'delete' && pages[op.page - 1]) {
          pdfDoc.removePage(op.page - 1);
        } else if (op.type === 'rotate' && pages[op.page - 1]) {
          const currentRotation = pages[op.page - 1].getRotation().angle;
          pages[op.page - 1].setRotation({ angle: currentRotation + op.degrees });
        } else if (op.type === 'add') {
          const { width, height } = pages[0]?.getSize() || { width: 612, height: 792 };
          pdfDoc.addPage([width, height]);
        }
      }
      
      // Re-get pages after operations
      const updatedPages = pdfDoc.getPages();
      
      // Apply text elements
      for (const textEl of textElements) {
        if (updatedPages[textEl.page - 1]) {
          const page = updatedPages[textEl.page - 1];
          const font = textEl.font === 'Helvetica-Bold' ? helveticaBoldFont : helveticaFont;
          const color = hexToRgb(textEl.color);
          page.drawText(textEl.text, {
            x: textEl.x,
            y: textEl.y,
            size: textEl.fontSize,
            font: font,
            color: rgb(color.r / 255, color.g / 255, color.b / 255)
          });
        }
      }
      
      // Apply redactions (black rectangles)
      for (const redact of redactions) {
        if (updatedPages[redact.page - 1]) {
          const page = updatedPages[redact.page - 1];
          page.drawRectangle({
            x: redact.x,
            y: redact.y,
            width: redact.width,
            height: redact.height,
            color: rgb(0, 0, 0)
          });
        }
      }
      
      // Apply images
      for (const imgEl of imageElements) {
        if (updatedPages[imgEl.page - 1]) {
          try {
            const page = updatedPages[imgEl.page - 1];
            let image;
            if (imgEl.imageData.startsWith('data:image/png')) {
              image = await pdfDoc.embedPng(imgEl.imageData);
            } else {
              image = await pdfDoc.embedJpg(imgEl.imageData);
            }
            page.drawImage(image, {
              x: imgEl.x,
              y: imgEl.y,
              width: imgEl.width,
              height: imgEl.height
            });
          } catch (err) {
            console.error('Failed to embed image:', err);
          }
        }
      }
      
      // Apply signatures
      for (const sig of signatures) {
        if (updatedPages[sig.page - 1]) {
          try {
            const page = updatedPages[sig.page - 1];
            const image = await pdfDoc.embedPng(sig.imageData);
            page.drawImage(image, {
              x: sig.x,
              y: sig.y,
              width: sig.width,
              height: sig.height
            });
          } catch (err) {
            console.error('Failed to embed signature:', err);
          }
        }
      }
      
      // Apply highlights
      for (const highlight of highlightedText) {
        if (updatedPages[highlight.page - 1]) {
          try {
            const page = updatedPages[highlight.page - 1];
            const rgbColor = hexToRgb(highlight.color);
            page.drawRectangle({
              x: highlight.x,
              y: highlight.y,
              width: highlight.width,
              height: highlight.height,
              color: rgb(rgbColor.r / 255, rgbColor.g / 255, rgbColor.b / 255),
              opacity: 0.3
            });
          } catch (err) {
            console.error('Failed to apply highlight:', err);
          }
        }
      }
      
      const editedBytes = await pdfDoc.save();
      
      // Download the edited PDF
      const blob = new Blob([editedBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName.replace('.pdf', '_edited.pdf') || 'edited_document.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      // Update local state with edited PDF
      const newPageCount = pdfDoc.getPageCount();
      setPdfBytes(editedBytes);
      const fileBlob = new Blob([editedBytes], { type: 'application/pdf' });
      const fileObj = new File([fileBlob], fileName, { type: 'application/pdf' });
      setPdfFile(fileObj);
      setNumPages(newPageCount);
      resetEditingState();
      setEditingMode(false);
      
      console.log('✅ [PDF Editor] PDF downloaded successfully');
    } catch (err) {
      console.error('❌ [PDF Editor] Download failed:', err);
      alert('Failed to download PDF. Check console for details.');
    } finally {
      setSaving(false);
    }
  }

  function hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 0, g: 0, b: 0 };
  }


  // No cleanup needed for Uint8Array/File objects

  return (
    <div className="pdf-editor-page">
      {/* Thumbnail Sidebar */}
      {pdfBytes && numPages && (
        <div className="pdf-thumbnail-sidebar">
          <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600' }}>Pages</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {Array.from({ length: numPages }, (_, i) => {
              const pageNum = i + 1;
              const thumbnail = thumbnailCache.get(pageNum);
              const isActive = currentPage === pageNum;
              
              return (
                <div
                  key={pageNum}
                  className={`pdf-thumbnail-item ${isActive ? 'active' : ''}`}
                  onClick={() => goToPage(pageNum)}
                  onMouseEnter={() => {
                    if (!thumbnail && pdfDoc) {
                      generateThumbnail(pageNum);
                    }
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    border: isActive ? '2px solid #B99040' : '2px solid transparent',
                    backgroundColor: isActive ? 'rgba(185, 144, 64, 0.1)' : 'transparent',
                    transition: 'all 0.2s'
                  }}
                >
                  {thumbnail ? (
                    <img 
                      src={thumbnail} 
                      alt={`Page ${pageNum}`}
                      style={{ 
                        width: '60px', 
                        height: 'auto', 
                        border: '1px solid #ddd',
                        borderRadius: '2px'
                      }} 
                    />
                  ) : (
                    <div style={{ 
                      width: '60px', 
                      height: '80px', 
                      background: '#f0f0f0', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      border: '1px solid #ddd',
                      borderRadius: '2px',
                      fontSize: '12px',
                      color: '#666'
                    }}>
                      {pageNum}
      </div>
                  )}
                  <span style={{ fontSize: '12px', fontWeight: isActive ? '600' : '400' }}>
                    Page {pageNum}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="pdf-main">
        {!pdfBytes ? (
          <div className="pdf-empty" style={{ textAlign: 'center', padding: '60px' }}>
            <h3>PDF Editor</h3>
            <p>Upload a PDF to get started</p>
            <input 
              type="file" 
              accept="application/pdf" 
              onChange={handleFile}
              style={{ 
                marginTop: '20px',
                padding: '10px',
                border: '2px dashed #B99040',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            />
            {loadingError && (
              <div style={{ marginTop: '12px', color: '#c62828', fontSize: '14px' }}>
                {loadingError}
              </div>
            )}
          </div>
        ) : (
          <div className="pdf-viewer">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: '12px' }}>
              <h2 style={{ margin: 0 }}>{fileName}</h2>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                {/* View Mode Selector */}
                <select
                  value={viewMode}
                  onChange={(e) => {
                    setViewMode(e.target.value);
                    if (e.target.value === 'single' && currentPage > numPages) {
                      setCurrentPage(1);
                    }
                  }}
                  style={{ padding: '6px 12px', border: '1px solid #ccc', borderRadius: 4 }}
                >
                  <option value="continuous">Continuous</option>
                  <option value="single">Single Page</option>
                  <option value="two-page">Two-Page Spread</option>
                </select>
                
                {/* Search Bar */}
                <div className="pdf-search-bar" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search PDF (Ctrl+F)"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      if (e.target.value.trim()) {
                        searchInPdf(e.target.value);
                      } else {
                        setSearchResults([]);
                      }
                    }}
                    style={{ padding: '4px 8px', border: '1px solid #ccc', borderRadius: 4, width: '200px' }}
                  />
                  {searchResults.length > 0 && (
                    <>
                      <span style={{ fontSize: '12px' }}>
                        {currentMatchIndex + 1} / {searchResults.length}
                      </span>
                      <button
                        onClick={() => navigateToMatch(Math.max(0, currentMatchIndex - 1))}
                        disabled={currentMatchIndex <= 0}
                        style={{ padding: '4px 8px', fontSize: '12px' }}
                      >
                        ↑
                      </button>
                      <button
                        onClick={() => navigateToMatch(Math.min(searchResults.length - 1, currentMatchIndex + 1))}
                        disabled={currentMatchIndex >= searchResults.length - 1}
                        style={{ padding: '4px 8px', fontSize: '12px' }}
                      >
                        ↓
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {!editingMode && (
                  <>
                    <button 
                      onClick={() => setEditingMode(true)}
                      style={{ padding: '8px 16px', background: '#B99040', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                    >
                      Edit PDF
                    </button>
                  </>
                )}
                {editingMode && (
                  <>
                    <button 
                      onClick={() => { resetEditingState(); setEditingMode(false); }}
                      style={{ padding: '8px 16px', background: '#666', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                    >
                      Cancel
                    </button>
                    <button 
                      onClick={handleDownload}
                      disabled={saving}
                      style={{ padding: '8px 16px', background: '#4caf50', color: 'white', border: 'none', borderRadius: 4, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}
                    >
                      {saving ? 'Processing...' : 'Download Edited PDF'}
                    </button>
                  </>
                )}
                <button 
                  onClick={handleClear}
                  style={{ padding: '8px 16px', background: '#c62828', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                >
                  Clear
                </button>
              </div>
            </div>

            {editingMode && (
              <div className="pdf-editor-toolbar">
                <div className="toolbar-section">
                  <button 
                    className={activeTool === TOOLS.SELECT ? 'active' : ''}
                    onClick={() => setActiveTool(TOOLS.SELECT)}
                    title="Select Tool"
                  >
                    ↖
                  </button>
                  <button 
                    className={activeTool === TOOLS.TEXT ? 'active' : ''}
                    onClick={() => setActiveTool(TOOLS.TEXT)}
                    title="Add Text"
                  >
                    T
                  </button>
                  <button 
                    className={activeTool === TOOLS.IMAGE ? 'active' : ''}
                    onClick={() => setActiveTool(TOOLS.IMAGE)}
                    title="Insert Image"
                  >
                    🖼
                  </button>
                  <button 
                    className={activeTool === TOOLS.REDACT ? 'active' : ''}
                    onClick={() => setActiveTool(TOOLS.REDACT)}
                    title="Redact"
                  >
                    █
                  </button>
                  <button 
                    className={activeTool === TOOLS.SIGNATURE ? 'active' : ''}
                    onClick={() => setActiveTool(TOOLS.SIGNATURE)}
                    title="Add Signature"
                  >
                    ✍
                  </button>
                  <button 
                    className={activeTool === TOOLS.HIGHLIGHT ? 'active' : ''}
                    onClick={() => setActiveTool(TOOLS.HIGHLIGHT)}
                    title="Highlight Text"
                  >
                    🖍
                  </button>
                </div>
                
                <div className="toolbar-section">
                  <button onClick={handleUndo} disabled={undoStack.length === 0} title="Undo">↶</button>
                  <button onClick={handleRedo} disabled={redoStack.length === 0} title="Redo">↷</button>
                </div>

                <div className="toolbar-section">
                  <label>Zoom: </label>
                  <button 
                    onClick={async () => {
                      const newZoom = await calculateFitWidthZoom();
                      setZoom(newZoom);
                    }}
                    title="Fit Width"
                    style={{ padding: '4px 8px', fontSize: '12px' }}
                  >
                    Fit W
                  </button>
                  <button 
                    onClick={async () => {
                      const newZoom = await calculateFitHeightZoom();
                      setZoom(newZoom);
                    }}
                    title="Fit Height"
                    style={{ padding: '4px 8px', fontSize: '12px' }}
                  >
                    Fit H
                  </button>
                  <button 
                    onClick={() => setZoom(1.0)}
                    title="Actual Size (100%)"
                    style={{ padding: '4px 8px', fontSize: '12px' }}
                  >
                    100%
                  </button>
                  <input 
                    type="range" 
                    min="0.5" 
                    max="3" 
                    step="0.1" 
                    value={zoom} 
                    onChange={(e) => setZoom(parseFloat(e.target.value))}
                    style={{ width: 100 }}
                  />
                  <span>{Math.round(zoom * 100)}%</span>
                </div>

                {activeTool === TOOLS.TEXT && (
                  <div className="toolbar-section">
                    <div style={{ marginBottom: 8, padding: 8, background: '#f0f0f0', borderRadius: 4, fontSize: 12 }}>
                      <strong>How to edit text:</strong>
                      <div style={{ marginTop: 4 }}>
                        1. Select text on the PDF page<br/>
                        2. Click anywhere or double-click to edit
                      </div>
                      <button 
                        onClick={() => {
                          const selection = window.getSelection();
                          if (selection && selection.toString().trim().length > 0) {
                            const range = selection.getRangeAt(0);
                            const rect = range.getBoundingClientRect();
                            const pageEl = document.querySelector('.react-pdf__Page__canvas')?.closest('div');
                            if (pageEl) {
                              const pageRect = pageEl.getBoundingClientRect();
                              const pageNum = parseInt(pageEl.getAttribute('data-page-number')) || 1;
                              const x = (rect.left - pageRect.left) / zoom;
                              const y = (pageRect.height - (rect.top - pageRect.top)) / zoom;
                              const width = rect.width / zoom;
                              const height = rect.height / zoom;
                              
                              setTextEdit({
                                show: true,
                                text: selection.toString(),
                                x,
                                y,
                                page: pageNum,
                                width,
                                height
                              });
                              selection.removeAllRanges();
                            }
                          } else {
                            alert('Please select some text first');
                          }
                        }}
                        style={{ marginTop: 8, padding: '6px 12px', background: '#4caf50', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                      >
                        Edit Selected Text
                      </button>
                    </div>
                    <label>Size: </label>
                    <input 
                      type="number" 
                      value={textProps.fontSize} 
                      onChange={(e) => setTextProps({...textProps, fontSize: parseInt(e.target.value) || 12})}
                      style={{ width: 60 }}
                    />
                    <label>Color: </label>
                    <input 
                      type="color" 
                      value={textProps.color} 
                      onChange={(e) => setTextProps({...textProps, color: e.target.value})}
                      style={{ width: 40 }}
                    />
                    <select 
                      value={textProps.font} 
                      onChange={(e) => setTextProps({...textProps, font: e.target.value})}
                    >
                      <option value="Helvetica">Helvetica</option>
                      <option value="Helvetica-Bold">Helvetica Bold</option>
                    </select>
                  </div>
                )}

                {activeTool === TOOLS.SIGNATURE && (
                  <div className="toolbar-section">
                    <div style={{ border: '1px solid #ccc', padding: 8, background: 'white' }}>
                      <canvas 
                        ref={signatureCanvasRef}
                        width={300}
                        height={120}
                        style={{ border: '1px solid #ddd', cursor: 'crosshair' }}
                        onMouseDown={(e) => {
                          const canvas = signatureCanvasRef.current;
                          if (!canvas) return;
                          const ctx = canvas.getContext('2d');
                          ctx.strokeStyle = '#000';
                          ctx.lineWidth = 2;
                          ctx.lineCap = 'round';
                          ctx.lineJoin = 'round';
                          const rect = canvas.getBoundingClientRect();
                          const x = e.clientX - rect.left;
                          const y = e.clientY - rect.top;
                          ctx.beginPath();
                          ctx.moveTo(x, y);
                          
                          const onMove = (e2) => {
                            const x2 = e2.clientX - rect.left;
                            const y2 = e2.clientY - rect.top;
                            ctx.lineTo(x2, y2);
                            ctx.stroke();
                          };
                          const onUp = () => {
                            document.removeEventListener('mousemove', onMove);
                            document.removeEventListener('mouseup', onUp);
                          };
                          document.addEventListener('mousemove', onMove);
                          document.addEventListener('mouseup', onUp);
                        }}
                      />
                      <button onClick={() => signatureCanvasRef.current?.getContext('2d').clearRect(0, 0, 300, 120)} style={{ marginTop: 4, padding: '4px 8px' }}>
                        Clear
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {editingMode && (
              <div className="page-management-panel">
                <h4>Page Management</h4>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {Array.from({ length: numPages || 0 }, (_, i) => i + 1).map(pageNum => (
                    <div key={pageNum} style={{ border: selectedPage === pageNum ? '2px solid #B99040' : '1px solid #ccc', padding: 8, borderRadius: 4, cursor: 'pointer' }} onClick={() => setSelectedPage(pageNum)}>
                      <div>Page {pageNum}</div>
                      <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                        <button onClick={(e) => { e.stopPropagation(); rotatePage(pageNum, 90); }} style={{ fontSize: 10, padding: '2px 4px' }}>↻</button>
                        <button onClick={(e) => { e.stopPropagation(); deletePage(pageNum); }} style={{ fontSize: 10, padding: '2px 4px', color: '#c62828' }}>×</button>
                      </div>
                    </div>
                  ))}
                  <button onClick={addBlankPage} style={{ padding: '8px 12px', border: '1px dashed #ccc', background: 'white', cursor: 'pointer' }}>
                    + Add Page
                  </button>
            </div>
          </div>
        )}

            {loadingError && <div style={{ color: '#c62828', marginBottom: 12 }}>Error: {loadingError}</div>}
            
            {/* Page Navigation Bar */}
            {pdfBytes && numPages && (
              <div className="pdf-navigation-bar">
                <button 
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage <= 1}
                  style={{ 
                    padding: '6px 12px', 
                    background: currentPage <= 1 ? '#ccc' : '#B99040', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: 4, 
                    cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
                    opacity: currentPage <= 1 ? 0.5 : 1
                  }}
                >
                  Previous
                </button>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>Page</span>
                  <input
                    type="number"
                    min="1"
                    max={numPages}
                    value={currentPage}
                    onChange={(e) => {
                      const page = parseInt(e.target.value);
                      if (page >= 1 && page <= numPages) {
                        goToPage(page);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const page = parseInt(e.target.value);
                        if (page >= 1 && page <= numPages) {
                          goToPage(page);
                        }
                      }
                    }}
                    style={{ 
                      width: '50px', 
                      padding: '4px 8px', 
                      border: '1px solid #ccc', 
                      borderRadius: 4,
                      textAlign: 'center'
                    }}
                  />
                  <span>of {numPages}</span>
      </div>
                <button 
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage >= numPages}
                  style={{ 
                    padding: '6px 12px', 
                    background: currentPage >= numPages ? '#ccc' : '#B99040', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: 4, 
                    cursor: currentPage >= numPages ? 'not-allowed' : 'pointer',
                    opacity: currentPage >= numPages ? 0.5 : 1
                  }}
                >
                  Next
                </button>
              </div>
            )}
            
            <div className="pdf-document" style={{ position: 'relative' }}>
              {!pdfBytes ? (
                <div>No PDF loaded</div>
              ) : (
              <div>
                  {numPages ? (() => {
                    // Define renderPageContent function first
                    function renderPageContent(pageNum) {
                      return (
                        <div style={{ position: 'relative', border: '1px solid #ccc', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', background: 'white' }}>
                          <canvas
                            id={`pdf-page-${pageNum}`}
                            ref={(el) => {
                              if (el && pdfDoc && !pageCanvasesRef.current[pageNum]) {
                                pageCanvasesRef.current[pageNum] = el;
                                const textLayerEl = document.getElementById(`pdf-text-layer-${pageNum}`);
                                renderPage(pageNum, el, textLayerEl);
                              }
                            }}
                            onClick={editingMode ? (e) => {
                              // Don't handle click if clicking on an element
                              if (e.target.closest('.pdf-element')) return;
                              handlePageClick(e, pageNum);
                            } : undefined}
                            onDoubleClick={editingMode && activeTool === TOOLS.TEXT ? (e) => handlePageDoubleClick(e, pageNum) : undefined}
                            style={{ 
                              cursor: editingMode && activeTool !== TOOLS.SELECT ? 'crosshair' : 'default',
                              display: 'block',
                              maxWidth: '100%',
                              height: 'auto',
                              position: 'relative'
                            }}
                          />
                          {/* Text layer for text selection */}
                          <div
                            id={`pdf-text-layer-${pageNum}`}
                            className="pdf-text-layer"
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              pointerEvents: editingMode ? 'none' : 'auto',
                              userSelect: editingMode ? 'none' : 'text',
                              zIndex: 1
                            }}
                          />
                          {!pdfDoc && (
                            <div style={{ 
                              position: 'absolute', 
                              top: '50%', 
                              left: '50%', 
                              transform: 'translate(-50%, -50%)',
                              padding: '20px',
                              background: 'rgba(255,255,255,0.9)',
                              borderRadius: '4px'
                            }}>
                              Loading page {pageNum}...
      </div>
                          )}
                          {editingMode && (
                            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none' }}>
                            {/* Text Elements */}
                            {textElements.filter(t => t.page === pageNum).map(textEl => {
                              const isSelected = selectedElement?.type === 'text' && selectedElement?.id === textEl.id;
                              return (
                                <div
                                  key={textEl.id}
                                  className="pdf-element"
                                  style={{
                                    position: 'absolute',
                                    left: `${textEl.x * zoom}px`,
                                    bottom: `${textEl.y * zoom}px`,
                                    fontSize: `${textEl.fontSize * zoom}px`,
                                    color: textEl.color,
                                    fontFamily: textEl.font === 'Helvetica-Bold' ? 'Helvetica, Arial, sans-serif' : 'Helvetica, Arial, sans-serif',
                                    fontWeight: textEl.font === 'Helvetica-Bold' ? 'bold' : 'normal',
                                    pointerEvents: 'auto',
                                    cursor: activeTool === TOOLS.SELECT ? 'move' : 'default',
                                    padding: isSelected ? '2px' : '0',
                                    border: isSelected ? '2px dashed #B99040' : 'none',
                                    backgroundColor: isSelected ? 'rgba(185, 144, 64, 0.1)' : 'transparent',
                                    userSelect: 'none'
                                  }}
                                  onMouseDown={(e) => {
                                    if (activeTool === TOOLS.SELECT) {
                                      handleElementMouseDown(e, 'text', textEl.id, textEl.x, textEl.y);
                                    } else if (e.detail === 2) {
                                      // Double click to edit
                                      setTextEdit({
                                        show: true,
                                        text: textEl.text,
                                        x: textEl.x,
                                        y: textEl.y,
                                        page: pageNum,
                                        width: 0,
                                        height: 0
                                      });
                                    }
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (activeTool === TOOLS.SELECT) {
                                      setSelectedElement({ type: 'text', id: textEl.id });
                                    } else if (window.confirm('Delete this text?')) {
                                      deleteElement('text', textEl.id);
                                    }
                                  }}
                                >
                                  {textEl.text}
                                  {isSelected && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        deleteElement('text', textEl.id);
                                      }}
                                      style={{
                                        position: 'absolute',
                                        top: '-8px',
                                        right: '-8px',
                                        width: '20px',
                                        height: '20px',
                                        borderRadius: '50%',
                                        background: '#c62828',
                                        color: 'white',
                                        border: 'none',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        lineHeight: '1',
                                        padding: 0
                                      }}
                                    >
                                      ×
                                    </button>
                                  )}
    </div>
  );
                            })}
                            
                            {/* Image Elements */}
                            {imageElements.filter(i => i.page === pageNum).map(imgEl => {
                              const isSelected = selectedElement?.type === 'image' && selectedElement?.id === imgEl.id;
                              return (
                                <div
                                  key={imgEl.id}
                                  className="pdf-element"
                                  style={{
                                    position: 'absolute',
                                    left: `${imgEl.x * zoom}px`,
                                    bottom: `${imgEl.y * zoom}px`,
                                    width: `${imgEl.width * zoom}px`,
                                    height: `${imgEl.height * zoom}px`,
                                    pointerEvents: 'auto',
                                    cursor: activeTool === TOOLS.SELECT ? 'move' : 'default',
                                    border: isSelected ? '2px dashed #B99040' : '1px solid transparent',
                                    backgroundColor: isSelected ? 'rgba(185, 144, 64, 0.1)' : 'transparent'
                                  }}
                                  onMouseDown={(e) => {
                                    if (activeTool === TOOLS.SELECT) {
                                      handleElementMouseDown(e, 'image', imgEl.id, imgEl.x, imgEl.y);
                                    }
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (activeTool === TOOLS.SELECT) {
                                      setSelectedElement({ type: 'image', id: imgEl.id });
                                    } else if (window.confirm('Delete this image?')) {
                                      deleteElement('image', imgEl.id);
                                    }
                                  }}
                                >
                                  <img
                                    src={imgEl.imageData}
                                    alt=""
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'contain',
                                      pointerEvents: 'none'
                                    }}
                                  />
                                  {isSelected && (
                                    <>
                                      {renderResizeHandles(imgEl, 'image', pageNum)}
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          deleteElement('image', imgEl.id);
                                        }}
                                        style={{
                                          position: 'absolute',
                                          top: '-8px',
                                          right: '-8px',
                                          width: '20px',
                                          height: '20px',
                                          borderRadius: '50%',
                                          background: '#c62828',
                                          color: 'white',
                                          border: 'none',
                                          cursor: 'pointer',
                                          fontSize: '12px',
                                          lineHeight: '1',
                                          padding: 0,
                                          zIndex: 1001
                                        }}
                                      >
                                        ×
                                      </button>
                                    </>
                                  )}
                                </div>
                              );
                            })}
                            
                            {/* Redactions */}
                            {redactions.filter(r => r.page === pageNum).map(redact => {
                              const isSelected = selectedElement?.type === 'redaction' && selectedElement?.id === redact.id;
                              return (
                                <div
                                  key={redact.id}
                                  className="pdf-element"
                                  style={{
                                    position: 'absolute',
                                    left: `${redact.x * zoom}px`,
                                    bottom: `${redact.y * zoom}px`,
                                    width: `${redact.width * zoom}px`,
                                    height: `${redact.height * zoom}px`,
                                    background: 'black',
                                    pointerEvents: 'auto',
                                    cursor: activeTool === TOOLS.SELECT ? 'move' : 'default',
                                    border: isSelected ? '2px dashed #B99040' : 'none'
                                  }}
                                  onMouseDown={(e) => {
                                    if (activeTool === TOOLS.SELECT) {
                                      handleElementMouseDown(e, 'redaction', redact.id, redact.x, redact.y);
                                    }
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (activeTool === TOOLS.SELECT) {
                                      setSelectedElement({ type: 'redaction', id: redact.id });
                                    } else if (window.confirm('Remove redaction?')) {
                                      deleteElement('redaction', redact.id);
                                    }
                                  }}
                                >
                                  {isSelected && (
                                    <>
                                      {renderResizeHandles(redact, 'redaction', pageNum)}
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          deleteElement('redaction', redact.id);
                                        }}
                                        style={{
                                          position: 'absolute',
                                          top: '-8px',
                                          right: '-8px',
                                          width: '20px',
                                          height: '20px',
                                          borderRadius: '50%',
                                          background: '#c62828',
                                          color: 'white',
                                          border: 'none',
                                          cursor: 'pointer',
                                          fontSize: '12px',
                                          lineHeight: '1',
                                          padding: 0,
                                          zIndex: 1001
                                        }}
                                      >
                                        ×
                                      </button>
                                    </>
                                  )}
                                </div>
                              );
                            })}
                            
                            {/* Signatures */}
                            {signatures.filter(s => s.page === pageNum).map(sig => {
                              const isSelected = selectedElement?.type === 'signature' && selectedElement?.id === sig.id;
                              return (
                                <div
                                  key={sig.id}
                                  className="pdf-element"
                                  style={{
                                    position: 'absolute',
                                    left: `${sig.x * zoom}px`,
                                    bottom: `${sig.y * zoom}px`,
                                    width: `${sig.width * zoom}px`,
                                    height: `${sig.height * zoom}px`,
                                    pointerEvents: 'auto',
                                    cursor: activeTool === TOOLS.SELECT ? 'move' : 'default',
                                    border: isSelected ? '2px dashed #B99040' : '1px solid transparent'
                                  }}
                                  onMouseDown={(e) => {
                                    if (activeTool === TOOLS.SELECT) {
                                      handleElementMouseDown(e, 'signature', sig.id, sig.x, sig.y);
                                    }
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (activeTool === TOOLS.SELECT) {
                                      setSelectedElement({ type: 'signature', id: sig.id });
                                    } else if (window.confirm('Delete this signature?')) {
                                      deleteElement('signature', sig.id);
                                    }
                                  }}
                                >
                                  <img
                                    src={sig.imageData}
                                    alt="Signature"
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'contain',
                                      pointerEvents: 'none'
                                    }}
                                  />
                                  {isSelected && (
                                    <>
                                      {renderResizeHandles(sig, 'signature', pageNum)}
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          deleteElement('signature', sig.id);
                                        }}
                                        style={{
                                          position: 'absolute',
                                          top: '-8px',
                                          right: '-8px',
                                          width: '20px',
                                          height: '20px',
                                          borderRadius: '50%',
                                          background: '#c62828',
                                          color: 'white',
                                          border: 'none',
                                          cursor: 'pointer',
                                          fontSize: '12px',
                                          lineHeight: '1',
                                          padding: 0,
                                          zIndex: 1001
                                        }}
                                      >
                                        ×
                                      </button>
                                    </>
                                  )}
                                </div>
                              );
                            })}
                            </div>
                          )}
                        </div>
                      );
                    }
                    
                    // Filter pages based on view mode
                    if (viewMode === 'single') {
                      return (
                        <div 
                          key={`page_${currentPage}`} 
                          data-page-number={currentPage}
                          style={{ position: 'relative', marginBottom: 16, display: 'flex', justifyContent: 'center' }}
                        >
                          {renderPageContent(currentPage)}
                        </div>
                      );
                    } else if (viewMode === 'two-page') {
                      const pages = [];
                      for (let i = 1; i <= numPages; i += 2) {
                        pages.push(
                          <div key={`group-${i}`} style={{ display: 'flex', gap: '16px', justifyContent: 'center', marginBottom: 16 }}>
                            {[i, i + 1].filter(p => p <= numPages).map(pageNum => (
                              <div key={`page_${pageNum}`} data-page-number={pageNum} style={{ position: 'relative' }}>
                                {renderPageContent(pageNum)}
                              </div>
                            ))}
            </div>
                        );
                      }
                      return pages;
                    } else {
                      // Continuous mode
                      return Array.from(new Array(numPages), (el, index) => {
                        const pageNum = index + 1;
                        return (
                          <div 
                            key={`page_${pageNum}`} 
                            data-page-number={pageNum}
                            style={{ position: 'relative', marginBottom: 16, display: 'flex', justifyContent: 'center' }}
                          >
                            {renderPageContent(pageNum)}
                          </div>
                        );
                      });
                    }
                  })() : (
                    <div style={{ padding: '40px', textAlign: 'center' }}>
                      {pdfDoc ? 'Loading pages...' : 'Failed to load PDF'}
                    </div>
                  )}
              </div>
              )}

              {textInput.show && (
                <TextInputDialog
                  onAdd={(text) => {
                    if (text.trim()) {
                      addTextElement(textInput.page, textInput.x, textInput.y, text);
                    }
                  }}
                  onCancel={() => setTextInput({ show: false, x: 0, y: 0, page: 1 })}
                />
              )}
              
              {textEdit.show && (
                <div style={{
                  position: 'fixed',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  background: 'white',
                  padding: 20,
                  border: '2px solid #B99040',
                  borderRadius: 8,
                  zIndex: 1001,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                  minWidth: 400
                }}>
                  <h3 style={{ marginTop: 0 }}>Edit Text</h3>
                  <textarea
                    value={textEdit.text}
                    onChange={(e) => setTextEdit({ ...textEdit, text: e.target.value })}
                    style={{ 
                      width: '100%', 
                      minHeight: 100, 
                      padding: 8, 
                      fontSize: 14,
                      border: '1px solid #ccc',
                      borderRadius: 4,
                      fontFamily: 'inherit'
                    }}
                    autoFocus
                  />
                  <div style={{ marginTop: 12, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button 
                      onClick={() => {
                        handleEditText(textEdit.text);
                      }} 
                      style={{ padding: '8px 16px', background: '#4caf50', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                    >
                      Replace Text
                    </button>
                    <button 
                      onClick={() => setTextEdit({ show: false, text: '', x: 0, y: 0, page: 1, width: 0, height: 0 })} 
                      style={{ padding: '8px 16px', background: '#666', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
